import { createSlice } from "@reduxjs/toolkit";

import { ELocalStoreKeys, ICategories, IUser } from "@/models/models";
import { setSession } from "@/utils/jwt";
import { getWithExpiry } from "@/utils/localStorage";

import {
  login,
  logout,
  my,
  refreshToken,
  register,
  resetPassword,
  updateEmail,
  updateMy,
  verifyAccountDelete,
  verifyCode,
} from "../actions/user";

export interface UserState {
  loading: boolean;
  user: IUser | null;
  categories: ICategories[];
}

const InitialCategories: ICategories[] = [];

// Cache the signed-in user in localStorage so a reload can rehydrate the
// logged-in state synchronously on first paint — no flash of the signed-out /
// FREE-plan UI while `/users/my` re-fetches. Only trusted when a valid
// (non-expired) access token is present.
const CACHED_USER_KEY = "as-cached-user";

const readCachedUser = (): IUser | null => {
  if (typeof window === "undefined") return null;
  try {
    if (!getWithExpiry(ELocalStoreKeys.TOKEN).value) return null;
    const raw = window.localStorage.getItem(CACHED_USER_KEY);
    return raw ? (JSON.parse(raw) as IUser) : null;
  } catch {
    return null;
  }
};

const writeCachedUser = (user: IUser | null): void => {
  if (typeof window === "undefined") return;
  try {
    if (user) window.localStorage.setItem(CACHED_USER_KEY, JSON.stringify(user));
    else window.localStorage.removeItem(CACHED_USER_KEY);
  } catch {
    /* localStorage blocked — skip the cache */
  }
};

const cachedUser = readCachedUser();
const initialState: UserState = {
  // With a cached user we already know the signed-in state, so we're not
  // "loading" — render it immediately and let the background my() refresh
  // reconcile silently.
  loading: !cachedUser,
  user: cachedUser,
  categories: InitialCategories,
};
const { actions, reducer } = createSlice({
  name: "user",
  initialState,
  reducers: {
    resetUserState: () => {
      writeCachedUser(null);
      return { loading: false, user: null, categories: InitialCategories };
    },
    setUserEmail: () => {},
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.fulfilled, (state, { payload }) => {
        if (!payload) {
          return;
        }
        state.user = payload.user as IUser;
        state.loading = false;
        writeCachedUser(payload.user as IUser);

        setSession({
          accessToken: payload.accessToken,
          refreshToken: payload.refreshToken,
          accessTokenSecondToExpiration: payload.accessTokenExpiresAt,
          refreshTokenSecondToExpiration: payload.refreshTokenExpiresAt,
        });
      })
      .addCase(register.fulfilled, (state, { payload }) => {
        if (!payload) {
          return;
        }
        state.user = payload.user as IUser;
        state.loading = false;
        writeCachedUser(payload.user as IUser);

        setSession({
          accessToken: payload.accessToken,
          refreshToken: payload.refreshToken,
          accessTokenSecondToExpiration: payload.accessTokenExpiresAt,
          refreshTokenSecondToExpiration: payload.refreshTokenExpiresAt,
        });
      })
      .addCase(refreshToken.fulfilled, (_state, { payload }) => {
        if (!payload) {
          return;
        }
        setSession({
          accessToken: payload.accessToken,
          refreshToken: payload.refreshToken,
          accessTokenSecondToExpiration: payload.accessTokenExpiresAt,
          refreshTokenSecondToExpiration: payload.refreshTokenExpiresAt,
        });
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.loading = false;

        // Preserve across logout/login:
        //   - cart (survives session)
        //   - persistent per-user subscription banner alerts (must
        //     survive logout/login until the user clicks × on the
        //     banner — see utils/subscriptionAlert)
        //   - pending-email-modal-closed (the user has already seen the
        //     verify modal; re-logging shouldn't force them to re-open
        //     it just to surface the reminder again)
        const cart = localStorage.getItem("as-cart");
        const pendingEmailModalClosed = localStorage.getItem("pending-email-modal-closed");
        // The Claims-page intro is a first-visit-per-browser onboarding modal.
        // Once the user has clicked "Got it" it must never auto-open again,
        // including after logging out and back in through the login gate.
        const claimsIntroSeen = localStorage.getItem("as-claims-intro-seen");
        const subAlerts: Record<string, string> = {};
        const cancelledDismisses: Record<string, string> = {};
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (!k) continue;
          if (k.startsWith("sub-action-alert-")) {
            const v = localStorage.getItem(k);
            if (v !== null) subAlerts[k] = v;
          } else if (k.startsWith("cancelled-banner-dismissed-")) {
            // Per-subscription-id dismiss flag for the global cancelled
            // banner — survives logout/login so re-logging on the same
            // browser doesn't resurrect a banner the user already X-ed.
            const v = localStorage.getItem(k);
            if (v !== null) cancelledDismisses[k] = v;
          }
        }
        localStorage.clear();
        if (cart) localStorage.setItem("as-cart", cart);
        if (pendingEmailModalClosed) localStorage.setItem("pending-email-modal-closed", pendingEmailModalClosed);
        if (claimsIntroSeen) localStorage.setItem("as-claims-intro-seen", claimsIntroSeen);
        for (const [k, v] of Object.entries(subAlerts)) localStorage.setItem(k, v);
        for (const [k, v] of Object.entries(cancelledDismisses)) localStorage.setItem(k, v);
      })
      .addCase(login.pending, (state) => {
        state.loading = true;
      })
      // A FAILED login (wrong credentials) must clear `loading`. Without this,
      // `login.pending` left `loading` stuck at `true` after the rejection, so
      // any `!isLoading` gate broke — notably the header rendered nothing (no
      // guest Sign In button) until a reload. Mirror for logout failures.
      .addCase(login.rejected, (state) => {
        state.loading = false;
      })
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateMy.fulfilled, (state, { payload }) => {
        if (!payload) {
          return;
        }

        state.user = payload as IUser;
        state.loading = false;
        writeCachedUser(payload as IUser);
      })
      .addCase(verifyCode.fulfilled, (state, { payload }) => {
        if (!payload) {
          return;
        }
        state.user = { ...state.user, isEmailVerified: true };
      })
      .addCase(my.fulfilled, (state, { payload }) => {
        if (!payload) {
          return;
        }
        state.user = payload as IUser;
        state.loading = false;
        writeCachedUser(payload as IUser);
      })
      // Logged-out (or expired-token) guests: the on-load `my()` probe 401s.
      // Without this, `loading` stayed at its initial `true` forever, so
      // `isLoading` never cleared for guests — and any gate that waits on
      // `!isLoading` silently broke (e.g. the favourite heart → "Sign Up to
      // Continue" dialog fell through to a createFavorites 401 instead of
      // opening). Only flip `loading`; don't null `user` here, so a transient
      // `my()` failure for a logged-in user doesn't visually log them out.
      .addCase(my.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateEmail.fulfilled, (state, { payload }) => {
        if (!payload) {
          return;
        }
        state.user = payload as IUser;
        state.loading = false;
        writeCachedUser(payload as IUser);
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.user = null;
        state.loading = false;

        const cart = localStorage.getItem("as-cart");
        localStorage.clear();
        if (cart) localStorage.setItem("as-cart", cart);
      })
      .addCase(verifyAccountDelete.fulfilled, (state) => {
        state.user = null;
        state.loading = false;

        const cart = localStorage.getItem("as-cart");
        localStorage.clear();
        if (cart) localStorage.setItem("as-cart", cart);
      });
  },
});

export const { resetUserState  } = actions;
export default reducer;
